import * as React from 'react';
import {
  Heading, Button, TextField, Row, Col,
} from '@elevationscu/ui-component-lib';
import styles from './loan-type.module.scss';
import ProgressBar from './ProgressBar';
import InputRequest from './input-request';
import DataDisclaimer from './data-disclaimer';
import SelectableButton from './SelectableButton';

function integerFormat(value) {
  if (!value) return value;
  // replaces all non integer characters
  const integer = value.replace(/[^\d]/g, '').toString();
  const modifiedInteger = integer.length <= 9 ? integer : integer.slice(0, 9);
  return `$ ${new Intl.NumberFormat('en-US').format(modifiedInteger)}`;
}

export default function LoanType({
  pageNumber, userInfo, setUserInfo, nextPage, previousPage, maxPages,
}) {
  const [userBudget, setUserBudget] = React.useState(null);
  const [downPayment, setDownPayment] = React.useState(null);
  const [loanArray, setLoanArray] = React.useState();
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
  const [downPaymentError, setDownPaymentError] = React.useState(null);
  const [budgetError, setBudgetError] = React.useState(null);
  const [selectedLoan, setSelectedLoan] = React.useState(
    {
      '30 year fixed': false,
      '20 year fixed': false,
      '15 year fixed': false,
      '10 year adjustable rate (ARM)': false,
      '7 year adjustable rate (ARM)': false,
      '5 year adjustable rate (ARM)': false,
      'Interest only adjustable rate (ARM)': false,
      "I'm not sure": false,
    },
  );

  const validationSchema = {
    downPayment: {
      required: 'A valid down payment amount is required.',
      maxLength: 10,
    },
    homePrice: {
      required: 'A valid property price is required.',
      maxLength: 10,
    },
  };

  React.useEffect(() => {
    if (userBudget !== null && userBudget !== '' && downPayment !== null && downPayment !== '' && userInfo.application_data.user_down_payment >= 0 && userInfo.application_data.user_budget >= 1 && loanArray.length > 0) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [selectedLoan, userBudget, downPayment, userInfo.application_data, loanArray]);

  React.useEffect(() => {
    if (!userInfo.application_data.loan_type) {
      userInfo.application_data.loan_type = [];
    }
    setLoanArray(userInfo.application_data.loan_type);

    userInfo.application_data.loan_type?.forEach((loan) => {
      if (userInfo.application_data.loan_type.includes(loan)) {
        selectedLoan[loan] = true;
      }
    });
    if (userInfo.application_data.user_budget) setUserBudget(integerFormat(userInfo.application_data.user_budget));
    if (userInfo.application_data.user_down_payment) setDownPayment(integerFormat(userInfo.application_data.user_down_payment));
    // eslint-disable-next-line
    }, [])

  function selectLoanType(loan) {
    // checks to see if loan is currently already in array to handle duplicates
    if (loanArray.includes(loan)) {
      const index = loanArray.indexOf(loan);
      if (index > -1) {
        const alteredLoanArray = loanArray.slice();
        alteredLoanArray.splice(index, 1);
        setLoanArray(alteredLoanArray);
      }
    } else {
      setLoanArray((array) => [...array, loan]);
    }
    setSelectedLoan({ ...selectedLoan, [loan]: !selectedLoan[loan] });
  }

  function next() {
    userInfo.application_data = Object.assign(userInfo.application_data, {
      loan_type: loanArray,
    });
    setUserInfo(userInfo);
    nextPage(userInfo);
  }

  function previous() {
    userInfo.application_data = Object.assign(userInfo.application_data, {
      loan_type: loanArray,
    });
    setUserInfo(userInfo);
    previousPage(userInfo);
  }

  function budgetAmount(e) {
    const formattedInteger = integerFormat(e.target.value);
    const insertedData = formattedInteger.replace(/\D/g, '');
    userInfo.application_data = Object.assign(userInfo.application_data, {
      user_budget: insertedData,
    });
    setUserBudget(formattedInteger);
  }

  function downPaymentInput(e) {
    const formattedInteger = integerFormat(e.target.value);
    const insertedData = formattedInteger.replace(/\D/g, '');
    userInfo.application_data = Object.assign(userInfo.application_data, {
      user_down_payment: insertedData,
    });
    setDownPayment(formattedInteger);
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.coreData}>
        <div className={styles.coreHeader}>
          <Heading size={2} element="h1" className={styles.loanTypeHeader}>
            Let's talk numbers
          </Heading>
          <InputRequest />
        </div>
        <div className={styles.prospectInput}>
          <ProgressBar
            pageNumber={pageNumber}
            maxPages={maxPages}
          />
          <Heading element="h2" size={3} className={styles.subHeader} id="PropertyPrice">
            How much do you expect to spend on the property?
          </Heading>
          <Row className={styles.indivInput}>
            <Col xs={12} lg={4}>
              <TextField
                id="LTUserBudget"
                value={userBudget}
                label="Estimated property price (required)"
                min="1"
                max="999999999"
                onChange={(e) => budgetAmount(e)}
                error={!budgetError ? budgetError : validationSchema.homePrice.required}
                onBlur={(e) => {
                  if (e.target.value !== null && e.target.value !== '' && userInfo.application_data.user_budget >= 1) {
                    setBudgetError(false);
                  } else {
                    setBudgetError(true);
                  }
                }}
              />
            </Col>
          </Row>

          <Heading element="h2" size={3} className={styles.topHeader} id="downPayment">
            How much do you expect to have for your down payment?
          </Heading>

          <Row className={styles.indivInput}>
            <Col xs={12} lg={4}>
              <TextField
                id="LTDownPayment"
                label="Down payment (required)"
                value={downPayment}
                min="1"
                max="999999999"
                onChange={(e) => downPaymentInput(e)}
                error={!downPaymentError ? downPaymentError : validationSchema.downPayment.required}
                onBlur={(e) => {
                  if (e.target.value !== null && e.target.value !== '' && userInfo.application_data.user_down_payment >= 0) {
                    setDownPaymentError(false);
                  } else {
                    setDownPaymentError(true);
                  }
                }}
              />
            </Col>
          </Row>
          <fieldset className={styles.fieldset}>
            <legend>
              <Heading element="h2" size={3} className={styles.subHeader} id="loanTypeSelection">
                What type(s) of loan are you interested in? (required)
              </Heading>
            </legend>
            <Row className={styles.loanRow}>
              {Object.keys(selectedLoan).map((loan) => (
                <SelectableButton
                  id={loan}
                  label={loan}
                  aria-label={loan}
                  aria-labelledby="loanTypeSelection"
                  onChange={() => selectLoanType(loan)}
                  checked={selectedLoan[loan]}
                />
              ))}
            </Row>
          </fieldset>
          <Row className={styles.rowSpacing}>
            <Col lg={8}>
              <DataDisclaimer />
            </Col>
            <Col className={styles.colSpacing}>
              <button onClick={previous} className={styles.previousButton} id="LTPrevButton" type="button">
                Previous
              </button>
              <Button
                onClick={next}
                className={styles.nextButton}
                id="LTNextButton"
                disabled={isButtonDisabled}
              >
                Next
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      {/* } */}
    </div>
  );
}
